import React from 'react';
import PropTypes from 'prop-types';
import { Prompt } from 'react-router-dom';
import { isEmpty } from 'utils/functions';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import DirectionsCar from '@mui/icons-material/DirectionsCar';
import CustomBackdrop from 'components/common/backdrop/CustomBackdrop';
import OfferAssetComponent from './OfferAssetComponent';
import AssetConfiguratorContainer from 'features/asset/configurator/components/AssetConfiguratorContainer';
import OfferAssetPropertiesHistoryModal from './OfferAssetPropertiesHistoryModal';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { CollapseCard } from '@totalsoft/rocket-ui';

const OfferAssetCard = React.memo(
  ({
    asset,
    hierarchy,
    currencies,
    assetTypes,
    assetIsLoaded,
    assetPropertiesAreLoaded,
    assetImage,
    onSave,
    onDuplicate,
    saving,
    duplicating,
    onCancel,
    onPropertyChange,
    t,
    loadPropertyDetails,
    loadPropertiesHistory,
    loadDealers,
    loadSuppliers,
    loadPartnerContactInfo,
    isSearchingRestrictedDealer,
    updatingChassisNumber,
    retrievingAssetData,
    handleChassisNumberSave,
    handleCancelChassisNumberSave,
    localChassisNumber,
    localSupplier,
    updateAssetChassisNo,
    financedAssetWrite,
    openOptions,
    handleOpenOptions,
    handleCloseOptions,
    openConfigurator,
    closeConfigurator,
    showConfigurator,
    openPropertiesHistory,
    showPropertiesHistory,
    closePropertiesHistory,
    maxMonths,
    selfPartnerName,
    enableConfiguratorButton,
    editRight,
    onAssetDataChange,
    splitAsset,
    processing,
    isFacilityFrame,
    hasWorkingHours,
    defaultValue
  }) => (
    <CollapseCard icon={DirectionsCar} title={t('OfferEdit.Asset.Name').toUpperCase()} onToggle={onCancel} defaultExpanded>
      {!assetIsLoaded || !asset || !asset.document || isEmpty(asset.document) ? (
        <WrappedFakeText lines={6} />
      ) : (
        <>
          <Prompt when={asset.dirtyInfo.isDirty} message={t('LeavingWithoutSaving')} />
          <OfferAssetComponent
            onAssetDataChange={onAssetDataChange}
            asset={asset}
            hierarchy={hierarchy}
            onPropertyChange={onPropertyChange}
            assetTypes={assetTypes}
            assetImage={assetImage}
            currencies={currencies}
            onSave={onSave}
            onDuplicate={onDuplicate}
            onCancel={onCancel}
            saving={saving}
            duplicating={duplicating}
            updatingChassisNumber={updatingChassisNumber}
            isDirty={asset.dirtyInfo.isDirty}
            loadPropertyDetails={loadPropertyDetails}
            handleChassisNumberSave={handleChassisNumberSave}
            handleCancelChassisNumberSave={handleCancelChassisNumberSave}
            loadDealers={loadDealers}
            loadSuppliers={loadSuppliers}
            loadPartnerContactInfo={loadPartnerContactInfo}
            isSearchingRestrictedDealer={isSearchingRestrictedDealer}
            localChassisNumber={localChassisNumber}
            localSupplier={localSupplier}
            updateAssetChassisNo={updateAssetChassisNo}
            financedAssetWrite={financedAssetWrite}
            openOptions={openOptions}
            maxMonths={maxMonths}
            selfPartnerName={selfPartnerName}
            handleOpenOptions={handleOpenOptions}
            handleCloseOptions={handleCloseOptions}
            openConfigurator={openConfigurator}
            openPropertiesHistory={openPropertiesHistory}
            enableConfiguratorButton={enableConfiguratorButton}
            editRight={editRight}
            processing={processing}
            splitAsset={splitAsset}
            isFacilityFrame={isFacilityFrame}
            hasWorkingHours={hasWorkingHours}
            defaultValue={defaultValue}
            assetPropertiesAreLoaded={assetPropertiesAreLoaded}
          />

          {showConfigurator && (
            <AssetConfiguratorContainer
              showConfigurator={showConfigurator}
              onCloseModal={closeConfigurator}
              asset={asset.document}
              editRight={editRight}
              isFacilityFrame={isFacilityFrame}
            />
          )}

          {showPropertiesHistory && (
            <OfferAssetPropertiesHistoryModal
              loadPropertiesHistory={loadPropertiesHistory}
              closeModal={closePropertiesHistory}
              assetId={asset.document.documentDetailId}
            />
          )}

          <CustomBackdrop message={t('OfferEdit.Asset.RetrievingAssetData')} open={retrievingAssetData}></CustomBackdrop>
        </>
      )}
    </CollapseCard>
  )
);

OfferAssetCard.displayName = 'OfferAssetCard';

OfferAssetCard.propTypes = {
  t: PropTypes.func.isRequired,
  asset: PropTypes.object,
  hierarchy: PropTypes.object,
  assetIsLoaded: PropTypes.bool.isRequired,
  assetPropertiesAreLoaded: PropTypes.bool.isRequired,
  currencies: PropTypes.array.isRequired,
  assetTypes: PropTypes.array.isRequired,
  assetImage: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  duplicating: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  loadPropertyDetails: PropTypes.func.isRequired,
  loadPropertiesHistory: PropTypes.func.isRequired,
  loadDealers: PropTypes.func.isRequired,
  loadSuppliers: PropTypes.func.isRequired,
  loadPartnerContactInfo: PropTypes.func.isRequired,
  isSearchingRestrictedDealer: PropTypes.bool,
  handleChassisNumberSave: PropTypes.func.isRequired,
  handleCancelChassisNumberSave: PropTypes.func.isRequired,
  updatingChassisNumber: PropTypes.bool,
  retrievingAssetData: PropTypes.bool,
  localChassisNumber: PropTypes.string,
  localSupplier: PropTypes.object,
  handleOpenOptions: PropTypes.func.isRequired,
  handleCloseOptions: PropTypes.func.isRequired,
  openOptions: PropTypes.bool.isRequired,
  financedAssetWrite: PropTypes.bool,
  updateAssetChassisNo: PropTypes.bool,
  openConfigurator: PropTypes.func.isRequired,
  closeConfigurator: PropTypes.func.isRequired,
  showConfigurator: PropTypes.bool.isRequired,
  openPropertiesHistory: PropTypes.func.isRequired,
  showPropertiesHistory: PropTypes.bool.isRequired,
  closePropertiesHistory: PropTypes.func.isRequired,
  maxMonths: PropTypes.number,
  selfPartnerName: PropTypes.string,
  enableConfiguratorButton: PropTypes.bool.isRequired,
  editRight: PropTypes.bool.isRequired,
  onAssetDataChange: PropTypes.func.isRequired,
  splitAsset: PropTypes.func.isRequired,
  processing: PropTypes.bool,
  isFacilityFrame: PropTypes.bool,
  hasWorkingHours: PropTypes.bool,
  defaultValue: PropTypes.string
};

export default compose(withTranslation()(OfferAssetCard));
